import { useState, useEffect } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
    Alert
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Send as SendIcon,
    Preview as PreviewIcon,
    Settings as SettingsIcon,
    Language as LanguageIcon
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import api from '@api/axios'
import { useTranslation } from '@i18n'
import { departmentsApi } from '@api/departments'
import { uploadApi } from '@api/upload'

export default function EmailManagement() {
    const { t, language } = useTranslation()

    // State
    const [templates, setTemplates] = useState([])
    const [departments, setDepartments] = useState([])
    const [selectedDepartments, setSelectedDepartments] = useState([])
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [customSubject, setCustomSubject] = useState('')
    const [customBody, setCustomBody] = useState('')
    const [emailLanguage, setEmailLanguage] = useState('en')
    const [loading, setLoading] = useState(false)
    const [templatesLoading, setTemplatesLoading] = useState(false)
    const [selectedUsersByDept, setSelectedUsersByDept] = useState({}) // Track selected users per department
    const [passwordsByDept, setPasswordsByDept] = useState({}) // Password per department
    const [ccEmail, setCcEmail] = useState('') // CC email for credentials

    // Dialogs
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
    const [sendDialogOpen, setSendDialogOpen] = useState(false)
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false)
    const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false)
    const [editingTemplate, setEditingTemplate] = useState(null)

    // Template form
    const [templateForm, setTemplateForm] = useState({
        name_en: '',
        name_ar: '',
        subject_en: '',
        subject_ar: '',
        body_en: '',
        body_ar: '',
        logo_url: '',
        header_color_start: '#1a3a6b',
        header_color_end: '#0d2247',
        show_header: true,
        template_language: 'both'
    })

    // Load data
    useEffect(() => {
        fetchTemplates()
        fetchDepartments()
    }, [])

    const fetchTemplates = async () => {
        setTemplatesLoading(true)
        try {
            const response = await api.get('/emails/templates')
            setTemplates(response.data || [])
        } catch (error) {
            console.error('Failed to fetch templates:', error)
            // Only show error if it's not a 404 (no templates yet)
            if (error.response?.status !== 404) {
                toast.error(language === 'ar' ? 'فشل تحميل القوالب' : 'Failed to load templates')
            }
            setTemplates([])
        } finally {
            setTemplatesLoading(false)
        }
    }

    const fetchDepartments = async () => {
        setLoading(true)
        try {
            // Fetch departments with users from the email endpoint
            const response = await api.get('/departments/emails')
            const depts = response.data || []
            setDepartments(depts)

            // Initialize selected users (all users selected by default)
            const userSelections = {}
            depts.forEach(dept => {
                if (dept.users && dept.users.length > 0) {
                    userSelections[dept.id] = dept.users.map(u => u.id)
                }
            })
            setSelectedUsersByDept(userSelections)
        } catch (error) {
            console.error('Failed to fetch departments:', error)
            toast.error(language === 'ar' ? 'فشل تحميل الأقسام' : 'Failed to load departments')
            setDepartments([])
        } finally {
            setLoading(false)
        }
    }

    // Template CRUD
    const handleCreateTemplate = () => {
        setEditingTemplate(null)
        setTemplateForm({
            name_en: '',
            name_ar: '',
            subject_en: '',
            subject_ar: '',
            body_en: '',
            body_ar: '',
            logo_url: '',
            header_color_start: '#1a3a6b',
            header_color_end: '#0d2247',
            show_header: true
        })
        setTemplateDialogOpen(true)
    }

    const handleEditTemplate = (template) => {
        setEditingTemplate(template)
        setTemplateForm(template)
        setTemplateDialogOpen(true)
    }

    const handleSaveTemplate = async () => {
        setLoading(true)
        try {
            if (editingTemplate) {
                await api.put(`/emails/templates/${editingTemplate.id}`, templateForm)
                toast.success(language === 'ar' ? 'تم تحديث القالب' : 'Template updated')
            } else {
                await api.post('/emails/templates', templateForm)
                toast.success(language === 'ar' ? 'تم إنشاء القالب' : 'Template created')
            }
            setTemplateDialogOpen(false)
            fetchTemplates()
        } catch (error) {
            console.error('Failed to save template:', error)
            toast.error(language === 'ar' ? 'فشل حفظ القالب' : 'Failed to save template')
        } finally {
            setLoading(false)
        }
    }

    const handleDeleteTemplate = async (id) => {
        if (!confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا القالب؟' : 'Are you sure you want to delete this template?')) {
            return
        }
        try {
            await api.delete(`/emails/templates/${id}`)
            toast.success(language === 'ar' ? 'تم حذف القالب' : 'Template deleted')
            fetchTemplates()
        } catch (error) {
            console.error('Failed to delete template:', error)
            toast.error(language === 'ar' ? 'فشل حذف القالب' : 'Failed to delete template')
        }
    }

    // Send credentials email
    const handleSendCredentials = async () => {
        if (selectedDepartments.length === 0) {
            toast.error(language === 'ar' ? 'الرجاء اختيار قسم واحد على الأقل' : 'Please select at least one department')
            return
        }

        // Open dialog to select users
        setCredentialsDialogOpen(true)
    }

    const handleConfirmSendCredentials = async () => {
        // Validate each department has a password
        const missingPasswords = selectedDepartments.filter(deptId => !passwordsByDept[deptId]?.trim())
        if (missingPasswords.length > 0) {
            toast.error(language === 'ar' ? 'يجب تعيين كلمة مرور لكل قسم' : 'Password required for each department')
            return
        }

        setLoading(true)
        try {
            // Build per-department data
            const departmentsData = selectedDepartments.map(deptId => ({
                department_id: deptId,
                user_ids: selectedUsersByDept[deptId] || [],
                password: passwordsByDept[deptId]?.trim()
            }))

            const payload = {
                departments: departmentsData,
                language: emailLanguage,
                cc_email: ccEmail.trim() || null
            }

            console.log('Sending credentials with payload:', payload)

            const response = await api.post('/emails/send-credentials', payload)

            toast.success(language === 'ar'
                ? `تم إرسال بيانات الدخول وتحديث كلمة المرور لـ ${response.data.success_count} قسم`
                : `Credentials sent and password updated for ${response.data.success_count} department(s)`)
            setCredentialsDialogOpen(false)
            setSelectedDepartments([])
            setPasswordsByDept({})
            setCcEmail('')
        } catch (error) {
            console.error('Failed to send credentials:', error)
            toast.error(language === 'ar' ? 'فشل إرسال بيانات الدخول' : 'Failed to send credentials')
        } finally {
            setLoading(false)
        }
    }
    const handleSendEmail = async () => {
        if (selectedDepartments.length === 0) {
            toast.error(language === 'ar' ? 'الرجاء اختيار قسم واحد على الأقل' : 'Please select at least one department')
            return
        }

        setLoading(true)
        try {
            const payload = {
                department_ids: selectedDepartments,
                template_id: selectedTemplate,
                custom_subject: customSubject,
                custom_body: customBody,
                language: emailLanguage
            }

            const response = await api.post('/emails/send-global', payload)
            toast.success(`${language === 'ar' ? 'تم إرسال' : 'Sent'} ${response.data.success_count} ${language === 'ar' ? 'رسالة بريد إلكتروني' : 'emails'}`)
            setSendDialogOpen(false)
            setSelectedDepartments([])
            setSelectedTemplate(null)
            setCustomSubject('')
            setCustomBody('')
        } catch (error) {
            console.error('Failed to send emails:', error)
            toast.error(language === 'ar' ? 'فشل إرسال البريد الإلكتروني' : 'Failed to send emails')
        } finally {
            setLoading(false)
        }
    }

    // Template columns
    const columns = [
        {
            field: 'name_en',
            headerName: language === 'ar' ? 'الاسم (EN)' : 'Name (EN)',
            flex: 1,
            minWidth: 150
        },
        {
            field: 'name_ar',
            headerName: language === 'ar' ? 'الاسم (AR)' : 'Name (AR)',
            flex: 1,
            minWidth: 150
        },
        {
            field: 'subject_en',
            headerName: language === 'ar' ? 'الموضوع (EN)' : 'Subject (EN)',
            flex: 1.5,
            minWidth: 200
        },
        {
            field: 'created_at',
            headerName: language === 'ar' ? 'تاريخ الإنشاء' : 'Created',
            width: 180,
            valueFormatter: (params) => new Date(params.value).toLocaleString(language)
        },
        {
            field: 'actions',
            headerName: language === 'ar' ? 'الإجراءات' : 'Actions',
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <IconButton size="small" color="primary" onClick={() => handleEditTemplate(params.row)}>
                        <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(params.row.id)}>
                        <DeleteIcon />
                    </IconButton>
                </Stack>
            )
        }
    ]

    const currentTemplate = templates.find(t => t.id === selectedTemplate)
    const previewSubject = customSubject || (currentTemplate ? (emailLanguage === 'ar' ? currentTemplate.subject_ar : currentTemplate.subject_en) : '')
    const previewBody = customBody || (currentTemplate ? (emailLanguage === 'ar' ? currentTemplate.body_ar : currentTemplate.body_en) : '')

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    {language === 'ar' ? '📧 إدارة البريد الإلكتروني العام' : '📧 Global Email Management'}
                </Typography>
            </Stack>

            <Grid container spacing={3}>
                {/* Send Email Section - Full Width */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                                {language === 'ar' ? 'إرسال بريد إلكتروني' : 'Send Email'}
                            </Typography>

                            <Grid container spacing={3}>
                                {/* Left Column - Department Selection */}
                                <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                        {language === 'ar' ? 'اختر الأقسام' : 'Select Departments'}
                                    </Typography>
                                    <Paper
                                        sx={{
                                            maxHeight: 400,
                                            overflow: 'auto',
                                            p: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'background.default'
                                        }}
                                    >
                                        {loading ? (
                                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                                {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
                                            </Typography>
                                        ) : departments.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                                {language === 'ar' ? 'لا توجد أقسام' : 'No departments found'}
                                            </Typography>
                                        ) : (
                                            <>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={selectedDepartments.length === departments.length}
                                                            indeterminate={selectedDepartments.length > 0 && selectedDepartments.length < departments.length}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedDepartments(departments.map(d => d.id))
                                                                } else {
                                                                    setSelectedDepartments([])
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label={<strong>{language === 'ar' ? 'تحديد الكل' : 'Select All'}</strong>}
                                                    sx={{ mb: 1, width: '100%', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}
                                                />
                                                {departments.map(dept => (
                                                    <FormControlLabel
                                                        key={dept.id}
                                                        control={
                                                            <Checkbox
                                                                checked={selectedDepartments.includes(dept.id)}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedDepartments([...selectedDepartments, dept.id])
                                                                    } else {
                                                                        setSelectedDepartments(selectedDepartments.filter(id => id !== dept.id))
                                                                    }
                                                                }}
                                                            />
                                                        }
                                                        label={language === 'ar' ? dept.name_ar : dept.name_en}
                                                        sx={{ width: '100%', ml: 0 }}
                                                    />
                                                ))}
                                            </>
                                        )}
                                    </Paper>
                                    {selectedDepartments.length > 0 && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            {selectedDepartments.length} {language === 'ar' ? 'قسم محدد' : 'department(s) selected'}
                                        </Alert>
                                    )}
                                </Grid>

                                {/* Right Column - Email Content */}
                                <Grid item xs={12} md={8}>
                                    <Stack spacing={2.5}>
                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                                {language === 'ar' ? 'إعدادات البريد' : 'Email Settings'}
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Select
                                                        value={selectedTemplate || ''}
                                                        onChange={(e) => setSelectedTemplate(e.target.value)}
                                                        displayEmpty
                                                        fullWidth
                                                        size="small"
                                                    >
                                                        <MenuItem value="">{language === 'ar' ? 'اختر قالب (اختياري)' : 'Select Template (Optional)'}</MenuItem>
                                                        {templates.map(template => (
                                                            <MenuItem key={template.id} value={template.id}>
                                                                {language === 'ar' ? template.name_ar : template.name_en}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Select
                                                        value={emailLanguage}
                                                        onChange={(e) => setEmailLanguage(e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                    >
                                                        <MenuItem value="en">🇬🇧 English</MenuItem>
                                                        <MenuItem value="ar">🇸🇾 العربية</MenuItem>
                                                    </Select>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        <TextField
                                            label={language === 'ar' ? 'الموضوع (اختياري)' : 'Subject (Optional)'}
                                            value={customSubject}
                                            onChange={(e) => setCustomSubject(e.target.value)}
                                            fullWidth
                                            size="small"
                                            placeholder={currentTemplate ? (emailLanguage === 'ar' ? currentTemplate.subject_ar : currentTemplate.subject_en) : ''}
                                        />

                                        <TextField
                                            label={language === 'ar' ? 'الرسالة (اختياري)' : 'Message (Optional)'}
                                            value={customBody}
                                            onChange={(e) => setCustomBody(e.target.value)}
                                            fullWidth
                                            multiline
                                            rows={8}
                                            placeholder={currentTemplate ? (emailLanguage === 'ar' ? currentTemplate.body_ar : currentTemplate.body_en) : ''}
                                        />

                                        <Stack direction="row" spacing={2}>
                                            <Button
                                                variant="contained"
                                                startIcon={<SendIcon />}
                                                onClick={() => setSendDialogOpen(true)}
                                                disabled={selectedDepartments.length === 0 || loading}
                                                fullWidth
                                                size="large"
                                            >
                                                {language === 'ar' ? 'إرسال بريد مخصص' : 'Send Custom Email'}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                startIcon={<SendIcon />}
                                                onClick={handleSendCredentials}
                                                disabled={selectedDepartments.length === 0 || loading}
                                                fullWidth
                                                size="large"
                                            >
                                                {language === 'ar' ? 'إرسال بيانات الدخول' : 'Send Credentials'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                startIcon={<PreviewIcon />}
                                                onClick={() => setPreviewDialogOpen(true)}
                                                disabled={loading}
                                            >
                                                {language === 'ar' ? 'معاينة' : 'Preview'}
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Templates Section - Full Width */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">
                                    {language === 'ar' ? 'قوالب البريد الإلكتروني' : 'Email Templates'}
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleCreateTemplate}
                                    disabled={loading}
                                >
                                    {language === 'ar' ? 'قالب جديد' : 'New Template'}
                                </Button>
                            </Stack>

                            <Box sx={{ height: 450, width: '100%' }}>
                                <DataGrid
                                    rows={templates}
                                    columns={columns}
                                    loading={templatesLoading}
                                    initialState={{
                                        pagination: {
                                            paginationModel: { pageSize: 10, page: 0 }
                                        }
                                    }}
                                    pageSizeOptions={[5, 10, 20, 50]}
                                    disableSelectionOnClick
                                    disableRowSelectionOnClick
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Template Dialog */}
            <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingTemplate
                        ? (language === 'ar' ? 'تعديل القالب' : 'Edit Template')
                        : (language === 'ar' ? 'قالب جديد' : 'New Template')}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label={language === 'ar' ? 'الاسم (EN)' : 'Name (EN)'}
                            value={templateForm.name_en}
                            onChange={(e) => setTemplateForm({ ...templateForm, name_en: e.target.value })}
                            fullWidth
                            required
                        />
                        <TextField
                            label={language === 'ar' ? 'الاسم (AR)' : 'Name (AR)'}
                            value={templateForm.name_ar}
                            onChange={(e) => setTemplateForm({ ...templateForm, name_ar: e.target.value })}
                            fullWidth
                            required
                        />
                        <TextField
                            select
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LanguageIcon sx={{ fontSize: 18 }} />
                                    {language === 'ar' ? 'لغة القالب' : 'Template Language'}
                                </Box>
                            }
                            value={templateForm.template_language || 'both'}
                            onChange={(e) => setTemplateForm({ ...templateForm, template_language: e.target.value })}
                            fullWidth
                        >
                            <MenuItem value="both">{language === 'ar' ? 'كلتا اللغتين' : 'Both Languages'}</MenuItem>
                            <MenuItem value="en">{language === 'ar' ? 'الإنجليزية فقط' : 'English Only'}</MenuItem>
                            <MenuItem value="ar">{language === 'ar' ? 'العربية فقط' : 'Arabic Only'}</MenuItem>
                        </TextField>
                        <TextField
                            label={language === 'ar' ? 'الموضوع (EN)' : 'Subject (EN)'}
                            value={templateForm.subject_en}
                            onChange={(e) => setTemplateForm({ ...templateForm, subject_en: e.target.value })}
                            fullWidth
                            required
                        />
                        <TextField
                            label={language === 'ar' ? 'الموضوع (AR)' : 'Subject (AR)'}
                            value={templateForm.subject_ar}
                            onChange={(e) => setTemplateForm({ ...templateForm, subject_ar: e.target.value })}
                            fullWidth
                            required
                        />
                        <TextField
                            label={language === 'ar' ? 'الرسالة (EN)' : 'Body (EN)'}
                            value={templateForm.body_en}
                            onChange={(e) => setTemplateForm({ ...templateForm, body_en: e.target.value })}
                            fullWidth
                            multiline
                            rows={4}
                            required
                        />
                        <TextField
                            label={language === 'ar' ? 'الرسالة (AR)' : 'Body (AR)'}
                            value={templateForm.body_ar}
                            onChange={(e) => setTemplateForm({ ...templateForm, body_ar: e.target.value })}
                            fullWidth
                            multiline
                            rows={4}
                            required
                        />

                        {/* Header Settings Section */}
                        <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SettingsIcon sx={{ color: 'primary.main', fontSize: 20 }} /> {language === 'ar' ? 'إعدادات الرأس' : 'Header Settings'}
                            </Typography>
                            <Stack spacing={2}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={templateForm.show_header !== false}
                                            onChange={(e) => setTemplateForm({ ...templateForm, show_header: e.target.checked })}
                                        />
                                    }
                                    label={language === 'ar' ? 'إظهار الرأس' : 'Show Header'}
                                />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" gutterBottom>
                                        {language === 'ar' ? 'شعار الرأس' : 'Header Logo'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            value={templateForm.logo_url || ''}
                                            onChange={(e) => setTemplateForm({ ...templateForm, logo_url: e.target.value })}
                                            fullWidth
                                            size="small"
                                            placeholder="https://example.com/logo.png"
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            component="label"
                                            sx={{ minWidth: 100 }}
                                        >
                                            {language === 'ar' ? 'رفع' : 'Upload'}
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0]
                                                    if (file) {
                                                        try {
                                                            const res = await uploadApi.upload(file)
                                                            setTemplateForm({ ...templateForm, logo_url: res.data.url })
                                                            toast.success(language === 'ar' ? 'تم رفع الشعار' : 'Logo uploaded')
                                                        } catch (err) {
                                                            toast.error(language === 'ar' ? 'فشل الرفع' : 'Upload failed')
                                                        }
                                                    }
                                                }}
                                            />
                                        </Button>
                                    </Box>
                                    {templateForm.logo_url && (
                                        <Box sx={{ mt: 1 }}>
                                            <img
                                                src={templateForm.logo_url}
                                                alt="Logo Preview"
                                                style={{ height: 'auto', maxWidth: '100%', objectFit: 'contain' }}
                                                onError={(e) => { e.target.style.display = 'none' }}
                                            />
                                        </Box>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {language === 'ar' ? 'اتركه فارغاً لاستخدام الشعار الافتراضي' : 'Leave empty for default logo'}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={2}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" color="text.secondary" gutterBottom>
                                            {language === 'ar' ? 'لون البداية' : 'Gradient Start'}
                                        </Typography>
                                        <TextField
                                            type="color"
                                            value={templateForm.header_color_start || '#1a3a6b'}
                                            onChange={(e) => setTemplateForm({ ...templateForm, header_color_start: e.target.value })}
                                            fullWidth
                                            size="small"
                                            sx={{ '& input': { height: 40, cursor: 'pointer' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" color="text.secondary" gutterBottom>
                                            {language === 'ar' ? 'لون النهاية' : 'Gradient End'}
                                        </Typography>
                                        <TextField
                                            type="color"
                                            value={templateForm.header_color_end || '#0d2247'}
                                            onChange={(e) => setTemplateForm({ ...templateForm, header_color_end: e.target.value })}
                                            fullWidth
                                            size="small"
                                            sx={{ '& input': { height: 40, cursor: 'pointer' } }}
                                        />
                                    </Box>
                                </Stack>
                                {/* Preview Bar */}
                                <Box sx={{
                                    background: `linear-gradient(135deg, ${templateForm.header_color_start || '#1a3a6b'} 0%, ${templateForm.header_color_end || '#0d2247'} 100%)`,
                                    borderRadius: 1,
                                    p: 2,
                                    textAlign: 'center'
                                }}>
                                    <Typography variant="caption" color="white">
                                        {language === 'ar' ? 'معاينة التدرج' : 'Gradient Preview'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTemplateDialogOpen(false)} disabled={loading}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleSaveTemplate}
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : (language === 'ar' ? 'حفظ' : 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Send Confirmation Dialog */}
            <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)}>
                <DialogTitle>{language === 'ar' ? 'تأكيد الإرسال' : 'Confirm Send'}</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {language === 'ar'
                            ? `سيتم إرسال البريد الإلكتروني إلى ${selectedDepartments.length} قسم`
                            : `Email will be sent to ${selectedDepartments.length} department(s)`}
                    </Alert>
                    <Typography variant="body2">
                        {language === 'ar' ? 'هل أنت متأكد من إرسال هذا البريد الإلكتروني؟' : 'Are you sure you want to send this email?'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSendDialogOpen(false)} disabled={loading}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleSendEmail}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') : (language === 'ar' ? 'إرسال' : 'Send')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{language === 'ar' ? 'معاينة البريد الإلكتروني' : 'Email Preview'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                                {language === 'ar' ? 'الموضوع:' : 'Subject:'}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {previewSubject || (language === 'ar' ? 'لا يوجد موضوع' : 'No subject')}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                                {language === 'ar' ? 'الرسالة:' : 'Message:'}
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'grey.50', whiteSpace: 'pre-wrap' }}>
                                {previewBody || (language === 'ar' ? 'لا توجد رسالة' : 'No message')}
                            </Paper>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewDialogOpen(false)}>
                        {language === 'ar' ? 'إغلاق' : 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Credentials Dialog - Select Users */}
            <Dialog open={credentialsDialogOpen} onClose={() => setCredentialsDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{language === 'ar' ? 'اختر المستخدمين لإرسال بيانات الدخول' : 'Select Users to Send Credentials'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={3}>
                        <Alert severity="info">
                            {language === 'ar'
                                ? 'سيتم إرسال بيانات الدخول إلى البريد الإلكتروني الخاص بكل قسم مع قائمة المستخدمين المحددين'
                                : 'Credentials will be sent to each department\'s login email with the list of selected users'}
                        </Alert>

                        {/* CC Email Field */}
                        <Paper sx={{ p: 2, bgcolor: 'info.lighter', border: '1px solid', borderColor: 'info.main' }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Typography variant="subtitle2" fontWeight="bold" color="info.dark" sx={{ whiteSpace: 'nowrap' }}>
                                    📋 {language === 'ar' ? 'نسخة كربونية (CC):' : 'CC (Carbon Copy):'}
                                </Typography>
                                <TextField
                                    value={ccEmail}
                                    onChange={(e) => setCcEmail(e.target.value)}
                                    placeholder={language === 'ar' ? 'أدخل بريد إلكتروني لاستلام نسخة من جميع الرسائل' : 'Enter email to receive copy of all emails'}
                                    size="small"
                                    fullWidth
                                    type="email"
                                    helperText={language === 'ar' ? 'اختياري - سيتم إرسال نسخة من جميع رسائل بيانات الدخول' : 'Optional - will receive copy of all credential emails'}
                                />
                            </Stack>
                        </Paper>
                        {selectedDepartments.map(deptId => {
                            const dept = departments.find(d => d.id === deptId)
                            if (!dept) return null

                            return (
                                <Paper key={deptId} sx={{ p: 2, border: '2px solid', borderColor: passwordsByDept[deptId]?.trim() ? 'success.main' : 'error.main' }}>
                                    <Stack spacing={2}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h6" gutterBottom>
                                                    {language === 'ar' ? dept.name_ar : dept.name_en}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    📧 {language === 'ar' ? 'البريد الإلكتروني:' : 'Login Email:'} {dept.login_email || (language === 'ar' ? 'غير محدد' : 'Not set')}
                                                </Typography>
                                            </Box>
                                            <TextField
                                                label={language === 'ar' ? '🔑 كلمة المرور' : '🔑 Password'}
                                                type="text"
                                                size="small"
                                                value={passwordsByDept[deptId] || ''}
                                                onChange={(e) => setPasswordsByDept({
                                                    ...passwordsByDept,
                                                    [deptId]: e.target.value
                                                })}
                                                required
                                                error={!passwordsByDept[deptId]?.trim()}
                                                placeholder={language === 'ar' ? 'أدخل كلمة المرور' : 'Enter password'}
                                                sx={{ width: 200, bgcolor: 'background.paper' }}
                                                InputProps={{
                                                    sx: { fontFamily: 'monospace', fontWeight: 'bold' }
                                                }}
                                            />
                                        </Box>

                                        {dept.users && dept.users.length > 0 ? (
                                            <Box>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={selectedUsersByDept[deptId]?.length === dept.users.length}
                                                            indeterminate={selectedUsersByDept[deptId]?.length > 0 && selectedUsersByDept[deptId]?.length < dept.users.length}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedUsersByDept({
                                                                        ...selectedUsersByDept,
                                                                        [deptId]: dept.users.map(u => u.id)
                                                                    })
                                                                } else {
                                                                    setSelectedUsersByDept({
                                                                        ...selectedUsersByDept,
                                                                        [deptId]: []
                                                                    })
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label={<strong>{language === 'ar' ? 'تحديد الكل' : 'Select All Users'}</strong>}
                                                />

                                                <Box sx={{ ml: 3 }}>
                                                    {dept.users.map(user => (
                                                        <FormControlLabel
                                                            key={user.id}
                                                            control={
                                                                <Checkbox
                                                                    checked={selectedUsersByDept[deptId]?.includes(user.id) || false}
                                                                    onChange={(e) => {
                                                                        const currentUsers = selectedUsersByDept[deptId] || []
                                                                        if (e.target.checked) {
                                                                            setSelectedUsersByDept({
                                                                                ...selectedUsersByDept,
                                                                                [deptId]: [...currentUsers, user.id]
                                                                            })
                                                                        } else {
                                                                            setSelectedUsersByDept({
                                                                                ...selectedUsersByDept,
                                                                                [deptId]: currentUsers.filter(id => id !== user.id)
                                                                            })
                                                                        }
                                                                    }}
                                                                />
                                                            }
                                                            label={
                                                                <Box>
                                                                    <Typography variant="body2">
                                                                        {language === 'ar' ? user.name_ar : user.name_en}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {user.email}
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                            sx={{ width: '100%', ml: 0 }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Alert severity="warning">
                                                {language === 'ar' ? 'لا يوجد مستخدمين لهذا القسم' : 'No users found for this department'}
                                            </Alert>
                                        )}
                                    </Stack>
                                </Paper>
                            )
                        })}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setCredentialsDialogOpen(false); setPasswordsByDept({}); setCcEmail(''); }} disabled={loading}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleConfirmSendCredentials}
                        variant="contained"
                        color="secondary"
                        disabled={loading || selectedDepartments.some(deptId => !passwordsByDept[deptId]?.trim())}
                        startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                        {loading ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') : (language === 'ar' ? 'إرسال بيانات الدخول' : 'Send Credentials')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
